import { errorStatus } from '../http';
import {
  SUMMARY_CAPABILITIES,
  type ConfigurationApiClient,
  type ConfigurationScope,
  type LayoutResponse,
  type ResolvedConfiguration,
} from './types';

/**
 * The backend configuration API, as plain functions over an injected client. Ported from
 * portal-ui-lib's `ConfigurationService` and `LayoutConfigurationApiService`; caching, which the
 * Angular `ConfigurationState` did by hand, is TanStack Query's job (./configuration-queries.ts).
 *
 * Not ported: the `/policy/tenant-capabilities` and `/policy/org-node-capabilities` writes (the
 * backend retired `/v1/policy`; entitlements replaced it), `getDateFormat` (its `DD/MM/YYYY`
 * fallback contradicts the Danish date rule), and the schema-registry reads nothing calls.
 */

/**
 * Both endpoints are user- and scope-sensitive and answer with `no-store`; asking the same of any
 * cache between us and them keeps a previous login's answer from being replayed.
 */
const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  Pragma: 'no-cache',
  Expires: '0',
};

/** The only schema version the backend serves; the Angular client always sent it too. */
const SCHEMA_VERSION = '1.0.0';

/** A resolve/layout body that is not the shape the backend contract promises. */
export class ConfigurationResponseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigurationResponseError';
  }
}

/**
 * `viewMode` travels only when it is `scoped`. Absent means the GLOBAL-admin bypass applies as
 * normal, which is also what the backend does for non-admins whatever is sent.
 */
function scopeQuery(scope: ConfigurationScope): Record<string, string | undefined> {
  return {
    orgNodeId: scope.orgNodeId ?? undefined,
    viewMode: scope.viewMode === 'scoped' ? 'scoped' : undefined,
  };
}

/**
 * Reject a body that would otherwise fail later and far away — a screen reading
 * `capabilities.view` off undefined. Same checks as the Angular service.
 */
export function assertResolvedConfiguration(body: unknown): asserts body is ResolvedConfiguration {
  if (!body || typeof body !== 'object') {
    throw new ConfigurationResponseError('Configuration response is empty');
  }
  const config = body as Partial<ResolvedConfiguration>;
  if (!config.meta || typeof config.meta !== 'object') {
    throw new ConfigurationResponseError('Configuration response is missing meta');
  }
  if (!config.capabilities || typeof config.capabilities !== 'object') {
    throw new ConfigurationResponseError('Configuration response is missing capabilities');
  }
  const capabilities = config.capabilities;
  const missing = SUMMARY_CAPABILITIES.filter(key => !(key in capabilities));
  if (missing.length > 0) {
    throw new ConfigurationResponseError(
      `Configuration response is missing capabilities: ${missing.join(', ')}`,
    );
  }
}

/** `GET /config/resolve` — one feature's configuration at the scope's org node. */
export async function resolveConfiguration(
  client: ConfigurationApiClient,
  feature: string,
  scope: ConfigurationScope,
  signal?: AbortSignal,
): Promise<ResolvedConfiguration> {
  const body = await client('/config/resolve', {
    headers: NO_CACHE_HEADERS,
    query: { feature, schemaVersion: SCHEMA_VERSION, ...scopeQuery(scope) },
    signal,
  });
  assertResolvedConfiguration(body);
  return body;
}

/**
 * `GET /config/layout` — the navigation for one app.
 *
 * `appId` goes in `X-App-Id`; the backend drops every item whose feature is not in that app's
 * membership (backend layout-app-map.ts), so the frontend never re-filters by feature.
 *
 * Unlike the Angular service, a failure is an error, not an empty layout: an empty sidebar that
 * looks like "you have access to nothing" is a worse answer than a visible failure.
 */
export async function fetchLayout(
  client: ConfigurationApiClient,
  appId: string,
  scope: ConfigurationScope,
  signal?: AbortSignal,
): Promise<LayoutResponse> {
  const body = await client<Partial<LayoutResponse> | null>('/config/layout', {
    headers: { ...NO_CACHE_HEADERS, 'X-App-Id': appId },
    query: scopeQuery(scope),
    signal,
  });
  if (!body || !body.layout || typeof body.layout !== 'object' || !body.meta) {
    throw new ConfigurationResponseError('Layout response is missing layout or meta');
  }
  return {
    layout: {
      sidebarSections: body.layout.sidebarSections ?? [],
      headerItems: body.layout.headerItems ?? [],
      headerConfig: body.layout.headerConfig ?? {},
    },
    capabilities: body.capabilities ?? {
      view: false,
      create: false,
      update: false,
      delete: false,
      export: false,
    },
    meta: body.meta,
  };
}

const preferencesPath = (userId: string) =>
  `/config/users/${encodeURIComponent(userId)}/preferences`;

/** `GET /config/users/:userId/preferences` — null when the user has saved none (404). */
export async function fetchUserPreferences(
  client: ConfigurationApiClient,
  userId: string,
  feature: string,
): Promise<unknown> {
  try {
    return await client(preferencesPath(userId), { query: { feature } });
  } catch (error) {
    if (errorStatus(error) === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * `PUT /config/users/:userId/preferences`. Preferences feed the resolution, so the caller must
 * invalidate the feature's configuration afterwards — `useSaveUserPreferences` does.
 */
export async function saveUserPreferences(
  client: ConfigurationApiClient,
  userId: string,
  feature: string,
  preferences: Record<string, unknown>,
): Promise<void> {
  await client(preferencesPath(userId), {
    method: 'PUT',
    query: { feature },
    json: { preferences },
  });
}
